import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarCheck, Clock, CreditCard } from "lucide-react";
import SearchBar from "@/components/site/SearchBar";
import YachtCard from "@/components/site/YachtCard";
import { HomeReviews } from "@/components/site/Reviews";
import { useLang } from "@/lib/i18n";
import { api, imgSrc, WHATSAPP_URL, CONTACT_EMAIL } from "@/lib/api";
import type { Tour, Yacht } from "@/types";

function Hero() {
  const { t } = useLang();
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden grain">
      <img src="/images/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/45 to-[#0B192C]/20" />
      <div className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pb-14 pt-32">
        <p className="eyebrow !text-[#F3E5AB] rise rise-1">{t("hero.eyebrow")}</p>
        <h1 data-testid="hero-title" className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white rise rise-2">{t("hero.title")}</h1>
        <p className="mt-6 max-w-xl text-base sm:text-lg text-white/80 rise rise-3">{t("hero.sub")}</p>
        <div className="mt-10 max-w-4xl rise rise-4"><SearchBar /></div>
        <div className="mt-8 flex flex-wrap gap-3 rise rise-4">
          {[{ i: Clock, k: "stat.blocks" }, { i: CalendarCheck, k: "stat.live" }, { i: CreditCard, k: "stat.pay" }].map(({ i: I, k }) => (
            <span key={k} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#0B192C]/50 backdrop-blur-md px-4 py-2 text-xs text-white/90">
              <I className="h-3.5 w-3.5 text-[#D4AF37]" />{t(k)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, sub, cta }: { eyebrow: string; title: string; sub?: string; cta?: React.ReactNode }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <div className="max-w-2xl">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] leading-tight">{title}</h2>
        {sub && <p className="mt-4 text-base text-slate-600">{sub}</p>}
      </div>
      {cta}
    </div>
  );
}

function Steps() {
  const { t } = useLang();
  return (
    <section className="bg-[#0B192C] text-white relative grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <p className="eyebrow !text-[#D4AF37]">{t("home.howEyebrow")}</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl">{t("home.how")}</h2>
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="border-t border-white/15 pt-8">
              <span className="font-display text-5xl text-[#00D2C8]">0{n}</span>
              <h3 className="mt-5 font-display text-2xl">{t(`home.step${n}`)}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/65">{t(`home.step${n}d`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, pick } = useLang();
  const { data: yachts = [] } = useQuery<Yacht[]>({ queryKey: ["yachts"], queryFn: async () => (await api.get("/yachts")).data });
  const { data: tours = [] } = useQuery<Tour[]>({ queryKey: ["tours"], queryFn: async () => (await api.get("/tours")).data });
  const featured = [...yachts].sort((a, b) => b.length_ft - a.length_ft).slice(0, 3);

  return (
    <div data-testid="home-page">
      <Hero />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <SectionHead eyebrow={t("home.featuredEyebrow")} title={t("home.featured")} sub={t("home.featuredSub")}
          cta={<Link to="/yachts" data-testid="home-view-all-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B192C] border-b border-[#D4AF37] pb-1 hover:text-[#008C86] transition-colors">{t("home.viewAll")}<ArrowRight className="h-4 w-4" /></Link>} />
        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          {featured[0] && <div className="lg:col-span-7"><YachtCard yacht={featured[0]} featured /></div>}
          <div className="lg:col-span-5 grid gap-8">
            {featured.slice(1).map((y) => <YachtCard key={y.id} yacht={y} featured />)}
          </div>
        </div>
      </section>
      <Steps />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <SectionHead eyebrow={t("home.toursEyebrow")} title={t("home.tours")}
          cta={<Link to="/tours" data-testid="home-tours-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B192C] border-b border-[#D4AF37] pb-1 hover:text-[#008C86] transition-colors">{t("home.toursCta")}<ArrowRight className="h-4 w-4" /></Link>} />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {tours.slice(0, 3).map((tour, i) => (
            <Link to="/tours" key={tour.id} className={`group relative overflow-hidden rounded-xl ${i === 0 ? "lg:row-span-2 aspect-[3/4] lg:aspect-auto" : "aspect-[4/3]"}`}>
              <img src={imgSrc(tour.image)} alt={pick(tour, "title")} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/85 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-[#00D2C8]">{pick(tour, "duration")}</p>
                <h3 className="mt-2 font-display text-2xl">{pick(tour, "title")}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <HomeReviews />
      <section className="relative overflow-hidden">
        <img src="/images/t-coast.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B192C]/80" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 text-white">
          <h2 className="max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl">{t("home.ctaTitle")}</h2>
          <p className="mt-4 max-w-xl text-white/75">{t("home.ctaSub")}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="home-cta-whatsapp" className="inline-flex justify-center rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">WhatsApp · 998 683 5267</a>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="home-cta-email" className="inline-flex justify-center rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white break-all transition-colors hover:bg-white/10">{CONTACT_EMAIL}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
